import { get, isPlainObject, transform } from 'lodash';
import * as FC from './form-condition';
import { z } from 'zod';
import { evaluateComparison, isCompareEquals, resolveComparisonTarget } from '../conditions/evaluate-comparison';
import { BLOCK_TITLE_INVALID_CHAR_RE, MAX_BLOCK_TITLE_LENGTH } from './generate-prompt';
const US_STATES = [
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'DC',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY'
];
const CA_PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];
export const stripNullValues = (obj) => {
    if (Array.isArray(obj))
        return obj.filter((item) => item !== null).map(stripNullValues);
    if (!isPlainObject(obj))
        return obj;
    return transform(obj, (result, value, key) => {
        if (value === null)
            return;
        if (typeof value === 'string' && value.trim() === '')
            return;
        result[key] = stripNullValues(value);
    }, {});
};
const collectLeavesDnf = (c) => {
    if ('always' in c || 'never' in c)
        return [];
    if ('allOf' in c) {
        let result = [[]];
        for (const child of c.allOf) {
            const childClauses = collectLeavesDnf(child);
            if (childClauses.length === 0)
                continue;
            const next = [];
            for (const existing of result) {
                for (const clause of childClauses) {
                    next.push([...existing, ...clause]);
                }
            }
            result = next;
        }
        return result;
    }
    if ('anyOf' in c)
        return c.anyOf.flatMap(collectLeavesDnf);
    return [[c]];
};
export const normalizeCondition = (condition) => {
    if (Array.isArray(condition))
        return condition;
    if ('allOf' in condition || 'anyOf' in condition || 'always' in condition || 'never' in condition)
        return collectLeavesDnf(condition);
    return [[condition]];
};
export const conditionFieldNames = (condition) => FC.fieldNames(FC.normalize(condition));
export const conditionLeafClauses = (condition) => FC.collectLeaves(FC.normalize(condition));
const resolveFieldName = (c, resolver) => resolver ? resolver(c) : (c.fieldName ?? 'unknown');
export const formatConditionGroup = (condition, fieldNameResolverOrOpts) => {
    const opts = typeof fieldNameResolverOrOpts === 'function'
        ? { fieldNameResolver: fieldNameResolverOrOpts }
        : (fieldNameResolverOrOpts ?? {});
    const normalized = FC.normalize(condition);
    if (!normalized)
        return '';
    return FC.format(normalized, (c, aud) => formatLeafCondition(c, opts.fieldNameResolver, aud), opts.audience);
};
export const COMPARISON_SYMBOL = {
    gt: '>',
    gte: '≥',
    lt: '<',
    lte: '≤'
};
const formatComparisonTarget = (c, fieldNameResolver) => {
    let base;
    if (c.valueSource === 'today') {
        base = 'today';
    }
    else if (c.valueSource === 'field' && c.expectedValueFieldId && fieldNameResolver) {
        base = fieldNameResolver({
            fieldId: c.expectedValueFieldId,
            fieldName: String(c.expectedValue)
        });
    }
    else {
        base = String(c.expectedValue);
    }
    if (!c.offset || c.offset === 0)
        return base;
    const sign = c.offset > 0 ? '+' : '−';
    const unit = c.coercion === 'days' ? ' days' : '';
    return `${base} ${sign} ${Math.abs(c.offset)}${unit}`;
};
export const formatLeafCondition = (c, fieldNameResolver, audience = 'llm') => {
    const name = resolveFieldName(c, fieldNameResolver);
    if (c.operation === 'gt' || c.operation === 'gte' || c.operation === 'lt' || c.operation === 'lte')
        return `${name} ${COMPARISON_SYMBOL[c.operation]} ${formatComparisonTarget(c, fieldNameResolver)}`;
    switch (c.operation) {
        case 'inlist':
            return `${name} is one of: {${c.expectedValues.join(', ')}}`;
        case 'not_inlist':
            return `${name} is not one of: {${c.expectedValues.join(', ')}}`;
        case 'has_any_of':
            return `${name} has any of: {${c.expectedValues.join(', ')}}`;
        case 'has_none_of':
            return `${name} has none of: {${c.expectedValues.join(', ')}}`;
        case 'equals':
        case 'not_equals': {
            if (isCompareEquals(c))
                return `${name} ${c.operation === 'equals' ? '=' : '≠'} ${formatComparisonTarget(c, fieldNameResolver)}`;
            const v = c.expectedValue;
            if (typeof v === 'boolean') {
                const boolLabel = audience === 'ui' ? (v ? 'yes' : 'no') : String(v);
                if (audience === 'ui')
                    return `${name} ${c.operation === 'equals' ? '=' : '≠'} ${boolLabel}`;
                return `${name} ${c.operation === 'equals' ? 'is' : 'is not'} ${boolLabel}`;
            }
            if (audience === 'ui')
                return `${name} ${c.operation === 'equals' ? '=' : '≠'} ${String(v)}`;
            return `${name} ${c.operation === 'equals' ? 'is' : 'is not'} ${String(v)}`;
        }
        case 'answered':
            return `${name} is answered`;
        case 'not_answered':
            return `${name} is not answered`;
        case 'begins_with':
        case 'not_begins_with':
        case 'ends_with':
        case 'not_ends_with':
            return FC.formatTextMatch(c, name);
    }
};
const formatOnlyIf = (lead, condition) => {
    const formatted = formatConditionGroup(condition);
    return formatted.includes('\n') ? `${lead} only if:\n${formatted}` : `${lead} only if ${formatted}.`;
};
const formatBound = (bound) => (bound === 'today' ? 'today' : String(bound));
const formatRangeConstraint = (range) => {
    const parts = [];
    if (range.minimum !== undefined)
        parts.push(`≥ ${formatBound(range.minimum)}`);
    if (range.exclusiveMinimum !== undefined)
        parts.push(`> ${formatBound(range.exclusiveMinimum)}`);
    if (range.maximum !== undefined)
        parts.push(`≤ ${formatBound(range.maximum)}`);
    if (range.exclusiveMaximum !== undefined)
        parts.push(`< ${formatBound(range.exclusiveMaximum)}`);
    return parts.length > 0 ? `Value must be ${parts.join(' and ')}` : undefined;
};
const buildFieldDescription = (field, suffix) => {
    if (!field.condition && !field.range && !suffix && !field.requiredWhen && !field.enumWhen)
        return field.description;
    const parts = [];
    if (field.condition)
        parts.push(formatOnlyIf('Fill this', field.condition));
    if (field.description)
        parts.push(field.description);
    if (field.requiredWhen)
        parts.push(formatOnlyIf('Required', field.requiredWhen));
    for (const [option, rule] of Object.entries(field.enumWhen ?? {}))
        parts.push(formatOnlyIf(`Offer "${option}"`, rule));
    if (field.range) {
        const rangeText = formatRangeConstraint(field.range);
        if (rangeText)
            parts.push(rangeText);
    }
    if (suffix)
        parts.push(suffix);
    if (parts.length === 0)
        return undefined;
    return parts.map((p) => p.trim().replace(/\.$/, '') + '.').join(' ');
};
// A list item's question tells the agent which of the item's other questions it waits on, by the name it fills.
const withSiblingNames = (field, siblings) => {
    const normalized = field.condition && FC.normalize(field.condition);
    if (!normalized)
        return field;
    const named = FC.mapLeaves(normalized, (c) => {
        const sibling = siblings.find((s) => conditionTargetsField(c, s));
        return sibling ? { ...c, fieldName: sibling.name } : c;
    });
    return { ...field, condition: named };
};
export const transformIntakeFormFieldToSchema = (field) => {
    switch (field.type) {
        case 'text':
        case 'textarea':
            return {
                type: 'string',
                description: buildFieldDescription(field)
            };
        case 'number':
            return {
                type: 'number',
                description: buildFieldDescription(field)
            };
        case 'boolean':
            return {
                type: 'boolean',
                description: buildFieldDescription(field)
            };
        case 'options':
            return {
                type: 'string',
                enum: field.enum || [],
                description: buildFieldDescription(field)
            };
        case 'list':
            return {
                type: 'array',
                items: field.items
                    ? transformIntakeFormFieldToSchema(field.items)
                    : undefined,
                description: buildFieldDescription(field)
            };
        case 'object':
            return {
                type: 'object',
                properties: field.properties
                    ? field.properties.reduce((acc, propField) => {
                        acc[propField.name] = transformIntakeFormFieldToSchema(withSiblingNames(propField, field.properties));
                        return acc;
                    }, {})
                    : {},
                description: buildFieldDescription(field)
            };
        case 'simpleDate':
            return {
                type: 'string',
                description: buildFieldDescription(field, 'Format: YYYY-MM-DD.')
            };
        case 'simpleTime':
            return {
                type: 'string',
                description: buildFieldDescription(field, 'Format: HH:MM (24-hour).')
            };
        case 'date':
            return {
                type: 'object',
                properties: {
                    timezone: {
                        description: "Fill based on the location. Make sure to ask for the state if you don't know it.",
                        type: 'string'
                    },
                    dateUTC: {
                        description: 'The date exactly as caller mentions. Make sure to convert to UTC ISO 8601 date format based on the timezone and local date. Format: YYYY-MM-DD',
                        type: 'string'
                    },
                    localDate: {
                        description: 'The date exactly as caller mentions. Format: YYYY-MM-DD',
                        type: 'string'
                    }
                },
                description: buildFieldDescription(field)
            };
        case 'datetime':
            return {
                type: 'object',
                properties: {
                    timezone: {
                        description: "Fill based on the location. Make sure to ask for the state if you don't know it.",
                        type: 'string'
                    },
                    dateAndTimeUTC: {
                        description: 'The date and time exactly as caller mentions. Make sure to convert to UTC ISO 8601 based on the timezone and local date and time. Format: YYYY-MM-DDTHH:MM:SSZ (e.g., 2024-01-15T15:30:00Z)',
                        type: 'string'
                    },
                    localDateAndTime: {
                        description: 'The date and time exactly as caller mentions. Format: YYYY-MM-DD HH:MM',
                        type: 'string'
                    },
                    dateTimeWithOffset: {
                        description: 'ISO 8601 datetime with timezone offset. Format: YYYY-MM-DDTHH:MM:SS±HH:MM (e.g., 2024-01-15T10:30:00-05:00)',
                        type: 'string'
                    }
                },
                description: buildFieldDescription(field)
            };
        case 'email':
            return {
                type: 'string',
                description: buildFieldDescription(field, 'Format as valid email.')
            };
        case 'phone':
            return {
                type: 'string',
                description: buildFieldDescription(field, '10 digit phone number.')
            };
        case 'ssn':
            return {
                type: 'string',
                description: buildFieldDescription(field, '9-digit Social Security Number.')
            };
        case 'phoneWithExtension':
            return {
                type: 'object',
                properties: {
                    phoneNumber: {
                        type: 'string',
                        description: '10 digit phone number.'
                    },
                    extension: {
                        type: 'string',
                        description: 'Optional extension number.'
                    }
                },
                description: buildFieldDescription(field, 'Phone number with optional extension.')
            };
        case 'address':
            return {
                type: 'object',
                properties: {
                    street1: {
                        description: 'Street address line 1.',
                        type: 'string'
                    },
                    street2: {
                        description: 'Street address line 2.',
                        type: 'string'
                    },
                    city: {
                        type: 'string'
                    },
                    state: {
                        description: 'Two-letter state/province code. If the caller mentions a city or postal code that makes the state obvious, fill it in. Otherwise, ask the caller.',
                        type: 'string',
                        enum: US_STATES.concat(CA_PROVINCES)
                    },
                    postalCode: {
                        description: 'Postal or ZIP code.',
                        type: 'string'
                    },
                    country: {
                        description: 'Two-letter country code. If not explicitly mentioned, make an educated guess based on other address details like state or postal code. Default to US if unable to determine.',
                        type: 'string',
                        enum: ['US', 'CA']
                    }
                },
                description: buildFieldDescription(field)
            };
        default:
            return {
                type: 'string',
                description: buildFieldDescription(field)
            };
    }
};
export const transformSchemaToIntakeFormField = (schema, fieldName = 'field') => {
    const id = crypto.randomUUID();
    const description = typeof schema === 'object' && 'description' in schema ? schema.description : undefined;
    // Check if it's a date object (has timezone, dateUTC, localDate)
    if (typeof schema === 'object' &&
        'type' in schema &&
        schema.type === 'object' &&
        'properties' in schema &&
        schema.properties) {
        const props = schema.properties;
        const propKeys = Object.keys(props);
        // Transform all properties first
        const properties = [];
        for (const [propName, propSchema] of Object.entries(props)) {
            properties.push(transformSchemaToIntakeFormField(propSchema, propName));
        }
        // Check for date structure
        if (propKeys.includes('timezone') && propKeys.includes('dateUTC') && propKeys.includes('localDate')) {
            return {
                id,
                name: fieldName,
                type: 'date',
                description,
                properties
            };
        }
        // Check for datetime structure
        if (propKeys.includes('timezone') &&
            propKeys.includes('dateAndTimeUTC') &&
            propKeys.includes('localDateAndTime')) {
            return {
                id,
                name: fieldName,
                type: 'datetime',
                description,
                properties
            };
        }
        // Check for address structure
        if (propKeys.includes('city') &&
            propKeys.includes('state') &&
            propKeys.includes('country') &&
            propKeys.includes('street1')) {
            return {
                id,
                name: fieldName,
                type: 'address',
                description,
                properties
            };
        }
        // Regular object
        return {
            id,
            name: fieldName,
            type: 'object',
            description,
            properties
        };
    }
    // Check if it's an array
    if (typeof schema === 'object' &&
        'type' in schema &&
        schema.type === 'array' &&
        'items' in schema &&
        schema.items) {
        return {
            id,
            name: fieldName,
            type: 'list',
            description,
            items: transformSchemaToIntakeFormField(schema.items, 'item')
        };
    }
    // Check if it's a string with enum
    if (typeof schema === 'object' &&
        'type' in schema &&
        schema.type === 'string' &&
        'enum' in schema &&
        schema.enum) {
        return {
            id,
            name: fieldName,
            type: 'options',
            description,
            enum: schema.enum
        };
    }
    // Check if it's a boolean
    if (typeof schema === 'object' && 'type' in schema && schema.type === 'boolean') {
        return {
            id,
            name: fieldName,
            type: 'boolean',
            description
        };
    }
    // Default to text
    return {
        id,
        name: fieldName,
        type: 'text',
        description
    };
};
const resolveDateBound = (bound) => {
    if (bound === 'today')
        return new Date(new Date().toISOString().slice(0, 10));
    return new Date(String(bound));
};
const resolveTimeBound = (bound) => {
    const s = String(bound);
    const [h, m] = s.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
};
const parseTimeToMinutes = (val) => {
    const match = val.match(/^(\d{1,2}):(\d{2})$/);
    if (!match)
        return -1;
    return Number(match[1]) * 60 + Number(match[2]);
};
const applyNumberRange = (v, range) => {
    if (!range)
        return v;
    if (range.minimum !== undefined)
        v = v.min(Number(range.minimum), { message: `Must be at least ${range.minimum}` });
    if (range.exclusiveMinimum !== undefined)
        v = v.gt(Number(range.exclusiveMinimum), { message: `Must be greater than ${range.exclusiveMinimum}` });
    if (range.maximum !== undefined)
        v = v.max(Number(range.maximum), { message: `Must be at most ${range.maximum}` });
    if (range.exclusiveMaximum !== undefined)
        v = v.lt(Number(range.exclusiveMaximum), { message: `Must be less than ${range.exclusiveMaximum}` });
    return v;
};
const dateRangeMessage = (range) => {
    const parts = [];
    if (range.minimum !== undefined)
        parts.push(`on or after ${formatBound(range.minimum)}`);
    if (range.exclusiveMinimum !== undefined)
        parts.push(`after ${formatBound(range.exclusiveMinimum)}`);
    if (range.maximum !== undefined)
        parts.push(`on or before ${formatBound(range.maximum)}`);
    if (range.exclusiveMaximum !== undefined)
        parts.push(`before ${formatBound(range.exclusiveMaximum)}`);
    return `Date must be ${parts.join(' and ')}`;
};
const applyDateRange = (v, range) => {
    if (!range)
        return v;
    const msg = dateRangeMessage(range);
    return v.refine((val) => {
        const d = new Date(val);
        if (range.minimum !== undefined && d < resolveDateBound(range.minimum))
            return false;
        if (range.exclusiveMinimum !== undefined && d <= resolveDateBound(range.exclusiveMinimum))
            return false;
        if (range.maximum !== undefined && d > resolveDateBound(range.maximum))
            return false;
        if (range.exclusiveMaximum !== undefined && d >= resolveDateBound(range.exclusiveMaximum))
            return false;
        return true;
    }, msg);
};
const timeRangeMessage = (range) => {
    const parts = [];
    if (range.minimum !== undefined)
        parts.push(`at or after ${range.minimum}`);
    if (range.exclusiveMinimum !== undefined)
        parts.push(`after ${range.exclusiveMinimum}`);
    if (range.maximum !== undefined)
        parts.push(`at or before ${range.maximum}`);
    if (range.exclusiveMaximum !== undefined)
        parts.push(`before ${range.exclusiveMaximum}`);
    return `Time must be ${parts.join(' and ')}`;
};
const applyTimeRange = (v, range) => {
    if (!range)
        return v;
    const msg = timeRangeMessage(range);
    return v.refine((val) => {
        const mins = parseTimeToMinutes(val);
        if (mins < 0)
            return true;
        if (range.minimum !== undefined && mins < resolveTimeBound(range.minimum))
            return false;
        if (range.exclusiveMinimum !== undefined && mins <= resolveTimeBound(range.exclusiveMinimum))
            return false;
        if (range.maximum !== undefined && mins > resolveTimeBound(range.maximum))
            return false;
        if (range.exclusiveMaximum !== undefined && mins >= resolveTimeBound(range.exclusiveMaximum))
            return false;
        return true;
    }, msg);
};
// path: where the field sits in the block's answers (a list inside an object is object.list).
const createFieldValidator = (field, context, path = field.name) => {
    let validator;
    switch (field.type) {
        case 'number':
            validator = applyNumberRange(z.number(), field.range);
            break;
        case 'boolean':
            validator = z.boolean();
            break;
        case 'email':
            validator = z.email();
            break;
        case 'phone':
            validator = z.string().regex(/^\d{10}$/, '10 digit phone number required');
            break;
        case 'ssn':
            validator = z.string().regex(/^\d{9}$/, '9-digit Social Security Number required');
            break;
        case 'phoneWithExtension':
            validator = z.object({
                phoneNumber: z.string().regex(/^\d{10}$/, '10 digit phone number required'),
                extension: z.string().optional()
            });
            break;
        case 'simpleDate':
            validator = applyDateRange(z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Format: YYYY-MM-DD'), field.range);
            break;
        case 'simpleTime':
            validator = applyTimeRange(z.string().regex(/^\d{1,2}:\d{2}$/, 'Format: HH:MM'), field.range);
            break;
        case 'date':
            validator = z.object({
                timezone: z.string(),
                dateUTC: z.string(),
                localDate: z.string()
            });
            break;
        case 'datetime':
            validator = z.object({
                timezone: z.string(),
                dateAndTimeUTC: z.string(),
                localDateAndTime: z.string(),
                dateTimeWithOffset: z.string()
            });
            break;
        case 'address':
            validator = z.object({
                city: z.string(),
                state: z.string(),
                country: z.string(),
                street1: z.string().optional(),
                street2: z.string().optional(),
                postalCode: z.string().optional()
            });
            break;
        case 'text':
            validator = z.string();
            break;
        case 'textarea':
            validator = z.string();
            break;
        case 'options':
            if (field.enum && field.enum.length > 0) {
                validator = z.enum(field.enum);
            }
            else if (field.enumWhen) {
                validator = z.string().refine(() => false, 'none of its options is offered right now');
            }
            else {
                validator = z.string();
            }
            break;
        case 'list':
            if (field.items) {
                validator = createListValidator(path, field.items, context);
            }
            else {
                validator = z.array(z.unknown());
            }
            break;
        case 'object':
            if (field.properties) {
                const shape = {};
                for (const propField of field.properties) {
                    shape[propField.name] = createFieldValidator(propField, context, `${path}.${propField.name}`);
                }
                validator = z.object(shape);
            }
            else {
                validator = z.record(z.string(), z.unknown());
            }
            break;
        default:
            validator = z.string();
    }
    if (!field.required) {
        validator = validator.optional();
    }
    return validator;
};
// A list item's question with a condition is required only in the items where it holds: each item is checked with its
// own index, so a question of the same list in the condition is read in that item (isFieldConditionMet).
const createListValidator = (list, items, context) => {
    const properties = items.properties ?? [];
    if (!properties.some((p) => p.condition))
        return z.array(createFieldValidator(items));
    const itemValidator = createFieldValidator({
        ...items,
        properties: properties.map((p) => (p.condition ? { ...p, required: false } : p))
    });
    return z.array(itemValidator).superRefine((answers, ctx) => answers.forEach((answer, index) => {
        const scope = { ...context, item: { list, index } };
        for (const p of properties) {
            const value = answer?.[p.name];
            if (p.required && p.condition && value == null && isFieldConditionMet(p, scope))
                ctx.addIssue({
                    code: 'invalid_type',
                    expected: 'string',
                    path: [index, p.name],
                    message: 'Required'
                });
        }
    }));
};
// A list of options' checks: whether any of the values is picked, or none is
export const isPickOp = (op) => op === 'has_any_of' || op === 'has_none_of';
// A check with a list of values: one answer among them (is one of), or a list of options' picks (has any of)
export const isListOp = (op) => op === 'inlist' || op === 'not_inlist' || isPickOp(op);
export const isPresenceOp = (op) => op === 'answered' || op === 'not_answered';
export const isComparisonOp = (op) => op === 'gt' || op === 'gte' || op === 'lt' || op === 'lte';
export const isTextMatchOp = (op) => op === 'begins_with' || op === 'not_begins_with' || op === 'ends_with' || op === 'not_ends_with';
export const isScalarCondition = (c) => c.operation === 'equals' || c.operation === 'not_equals';
export const isListCondition = (c) => isListOp(c.operation);
export const isPickCondition = (c) => isPickOp(c.operation);
export const isComparisonCondition = (c) => isComparisonOp(c.operation);
export const isTextMatchCondition = (c) => isTextMatchOp(c.operation);
// Begins with / ends with, case-sensitive like Smart.ly's left / right compare unless ignoreCase (the script cased both
// sides with toUpperCase / toLowerCase).
export const evaluateTextMatch = (value, { operation, expectedValue, ignoreCase }) => {
    const fold = (s) => (ignoreCase ? s.toLowerCase() : s);
    const text = fold(value == null ? '' : String(value));
    const target = fold(expectedValue);
    const matches = operation === 'begins_with' || operation === 'not_begins_with'
        ? text.startsWith(target)
        : text.endsWith(target);
    return operation === 'begins_with' || operation === 'ends_with' ? matches : !matches;
};
export const operationMode = (op) => {
    if (isPresenceOp(op))
        return 'presence';
    if (isListOp(op))
        return 'list';
    if (isComparisonOp(op))
        return 'comparison';
    return 'scalar';
};
export const availableOperations = (field) => {
    const fieldType = field?.type;
    const ops = [];
    if (fieldType === 'boolean' || fieldType === 'options' || fieldType === 'text')
        ops.push('equals', 'not_equals');
    if (fieldType === 'options' || fieldType === 'text')
        ops.push('inlist', 'not_inlist');
    if (fieldType === 'list' && field?.items?.type === 'options')
        ops.push('has_any_of', 'has_none_of');
    ops.push('answered', 'not_answered');
    return ops;
};
export const availableValues = (field) => {
    if (field.type === 'boolean')
        return ['Yes', 'No'];
    if (field.type === 'list')
        return field.items?.enum ?? [];
    return field.enum ?? [];
};
export const OPERATION_LABEL = {
    equals: 'is',
    not_equals: 'is not',
    inlist: 'is one of',
    not_inlist: 'is not one of',
    has_any_of: 'has any of',
    has_none_of: 'has none of',
    answered: 'is answered',
    not_answered: 'is not answered',
    gt: 'greater than',
    gte: 'greater than or equal',
    lt: 'less than',
    lte: 'less than or equal',
    ...FC.TEXT_MATCH_LABEL
};
export const OPERATION_RANK = {
    equals: 0,
    not_equals: 1,
    inlist: 2,
    not_inlist: 3,
    answered: 4,
    not_answered: 5,
    gt: 6,
    gte: 7,
    lt: 8,
    lte: 9,
    begins_with: 10,
    not_begins_with: 11,
    ends_with: 12,
    not_ends_with: 13,
    has_any_of: 14,
    has_none_of: 15
};
export const payloadFromCondition = (c) => {
    if (isListCondition(c))
        return { kind: 'list', operation: c.operation, values: c.expectedValues };
    if (isPresenceOp(c.operation))
        return { kind: 'presence', operation: c.operation };
    if (isScalarCondition(c))
        return { kind: 'scalar', operation: c.operation, value: c.expectedValue };
    return { kind: 'presence', operation: 'answered' };
};
export const conditionFromPayload = (gateName, p, gateFieldId) => {
    const base = gateFieldId ? { fieldId: gateFieldId, fieldName: gateName } : { fieldName: gateName };
    if (p.kind === 'list')
        return { ...base, operation: p.operation, expectedValues: p.values };
    if (p.kind === 'presence')
        return { ...base, operation: p.operation };
    return { ...base, operation: p.operation, expectedValue: p.value };
};
export const formatScalarValue = (v) => {
    if (v === undefined)
        return '';
    if (typeof v === 'boolean')
        return v ? 'Yes' : 'No';
    return String(v);
};
export const seedListFromCondition = (prev) => {
    if (isListCondition(prev))
        return prev.expectedValues;
    if (isPresenceOp(prev.operation))
        return [];
    if ('expectedValue' in prev && typeof prev.expectedValue === 'string')
        return [prev.expectedValue];
    return [];
};
const readFieldPath = (context, path) => {
    if (!context.blockData)
        return undefined;
    const { item } = context;
    if (!item || !path.startsWith(`${item.list}.`))
        return get(context.blockData, path);
    return get(context.blockData, [
        ...item.list.split('.'),
        item.index,
        ...path.slice(item.list.length + 1).split('.')
    ]);
};
const evaluateCompare = (condition, fieldValue, ctx) => evaluateComparison({
    operation: condition.operation,
    fieldValue: fieldValue == null ? undefined : String(fieldValue),
    targetValue: resolveComparisonTarget({
        expectedValue: String(condition.expectedValue),
        expectedValueFieldId: condition.expectedValueFieldId,
        valueSource: condition.valueSource,
        getFieldValue: (name) => ctx.resolveField?.(name),
        findFieldById: ctx.findFieldById
    }),
    coercion: condition.coercion,
    offset: condition.offset
});
// A missing value, the empty value and a list with nothing in it hold no answer
const isNoAnswer = (value) => value == null || value === '' || (Array.isArray(value) && value.length === 0);
const evaluateCondition = (condition, fieldValue, ctx = {}) => {
    switch (condition.operation) {
        case 'gt':
        case 'gte':
        case 'lt':
        case 'lte':
            return evaluateCompare(condition, fieldValue, ctx);
        case 'begins_with':
        case 'not_begins_with':
        case 'ends_with':
        case 'not_ends_with':
            return evaluateTextMatch(fieldValue, condition);
        case 'answered':
            return !isNoAnswer(fieldValue);
        case 'not_answered':
            return isNoAnswer(fieldValue);
        case 'inlist': {
            const actual = fieldValue == null ? '' : String(fieldValue);
            return condition.expectedValues.some((v) => v === actual);
        }
        case 'not_inlist': {
            const actual = fieldValue == null ? '' : String(fieldValue);
            return !condition.expectedValues.some((v) => v === actual);
        }
        // A list with nothing picked holds no pick; one pick saved as text is that one pick
        case 'has_any_of':
        case 'has_none_of': {
            const picks = Array.isArray(fieldValue)
                ? fieldValue
                : fieldValue == null || fieldValue === ''
                    ? []
                    : [fieldValue];
            const anyPicked = picks.some((pick) => condition.expectedValues.includes(String(pick)));
            return condition.operation === 'has_any_of' ? anyPicked : !anyPicked;
        }
        case 'equals':
        case 'not_equals': {
            if (isCompareEquals(condition))
                return evaluateCompare(condition, fieldValue, ctx);
            if (fieldValue == null)
                return condition.operation === 'not_equals';
            const expected = condition.expectedValue;
            let normalized = fieldValue;
            if (typeof expected === 'boolean') {
                if (typeof fieldValue === 'boolean')
                    normalized = fieldValue;
                else if (typeof fieldValue === 'string' && fieldValue !== '') {
                    const lower = fieldValue.toLowerCase();
                    normalized = lower === 'yes' || lower === 'true';
                }
            }
            return condition.operation === 'equals' ? normalized === expected : normalized !== expected;
        }
    }
};
const resolveLeafFieldValue = (c, context) => {
    if (c.fieldId && context.fields) {
        const path = findFieldPath(context.fields, c.fieldId);
        if (path)
            return readFieldPath(context, path);
    }
    if (c.fieldName)
        return context.blockData ? get(context.blockData, c.fieldName) : undefined;
    return undefined;
};
export const isFieldConditionMet = (field, context) => {
    if (!field.condition)
        return true;
    const normalized = FC.normalize(field.condition);
    if (!normalized)
        return true;
    const resolveField = (name) => {
        const v = context.blockData ? get(context.blockData, name) : undefined;
        return v == null ? undefined : String(v);
    };
    const findFieldById = (id) => {
        const path = context.fields && findFieldPath(context.fields, id);
        if (!path)
            return undefined;
        const v = readFieldPath(context, path);
        return v == null ? undefined : String(v);
    };
    const helpers = { resolveField, findFieldById };
    return FC.evaluate(normalized, (leaf) => evaluateCondition(leaf, resolveLeafFieldValue(leaf, context), helpers));
};
// The options and requiredness the answers so far give it
export const applyFieldRules = (field, context) => {
    const { intakeData, blocks } = context;
    if (!intakeData || (!field.enumWhen && !field.requiredWhen))
        return field;
    return {
        ...field,
        ...(field.enumWhen && { enum: getOfferedOptions(field, intakeData, blocks) }),
        required: isFieldRequired(field, intakeData, blocks)
    };
};
export const createBlockValidator = (block, context = {}) => {
    const shape = {};
    const ctx = { ...context, fields: context.fields ?? block.fields };
    for (const field of block.fields) {
        const ruled = applyFieldRules(field, ctx);
        const fieldForValidation = field.condition && !isFieldConditionMet(field, ctx) ? { ...ruled, required: false } : ruled;
        shape[field.name] = createFieldValidator(fieldForValidation, ctx);
    }
    return z.object(shape);
};
export const conditionTargetsField = (condition, field) => condition.fieldId && field.id
    ? condition.fieldId === field.id
    : condition.fieldName === field.name || condition.fieldName === field.sourceKey;
export const resolveConditionField = (condition, fields) => {
    for (const f of fields) {
        if (conditionTargetsField(condition, f))
            return f;
        if (f.properties) {
            const nested = resolveConditionField(condition, f.properties);
            if (nested)
                return nested;
        }
        if (f.items?.properties) {
            const nested = resolveConditionField(condition, f.items.properties);
            if (nested)
                return nested;
        }
    }
    return undefined;
};
export const findFieldDeep = resolveConditionField;
const findFieldPath = (fields, fieldId, prefix = '') => {
    for (const f of fields) {
        const path = prefix ? `${prefix}.${f.name}` : f.name;
        if (f.id === fieldId)
            return path;
        if (f.properties) {
            const nested = findFieldPath(f.properties, fieldId, path);
            if (nested)
                return nested;
        }
        if (f.items?.properties) {
            const nested = findFieldPath(f.items.properties, fieldId, path);
            if (nested)
                return nested;
        }
    }
    return undefined;
};
export const buildFieldIdMap = (blocks) => {
    const map = new Map();
    const walk = (fields) => {
        for (const f of fields) {
            if (f.id && f.name)
                map.set(f.id, f.name);
            if (f.properties)
                walk(f.properties);
            if (f.items?.properties)
                walk(f.items.properties);
        }
    };
    for (const block of blocks)
        walk(block.fields);
    return map;
};
// A field's options rules, without the ones a patch leaves empty: those options are then always offered
const mapEnumWhen = (enumWhen, patch) => Object.fromEntries(Object.entries(enumWhen).flatMap(([option, rule]) => {
    const patched = patch(rule);
    return patched ? [[option, patched]] : [];
}));
// Each field's condition and rules, and its list items' (an item's question shown by another answer in the same item).
const mapFieldConditions = (fields, patch) => fields.map((f) => {
    const patched = f.condition || f.requiredWhen || f.enumWhen
        ? {
            ...f,
            ...(f.condition && { condition: patch(f.condition) }),
            ...(f.requiredWhen && { requiredWhen: patch(f.requiredWhen) }),
            ...(f.enumWhen && { enumWhen: mapEnumWhen(f.enumWhen, patch) })
        }
        : f;
    return patched.items?.properties
        ? {
            ...patched,
            items: { ...patched.items, properties: mapFieldConditions(patched.items.properties, patch) }
        }
        : patched;
});
export const mapBuilderConditions = (builder, patchLeaf) => {
    const patchCondition = (c) => {
        if (!c)
            return c;
        const normalized = FC.normalize(c);
        if (!normalized)
            return c;
        return FC.mapLeaves(normalized, patchLeaf);
    };
    return {
        ...builder,
        blocks: (builder.blocks ?? []).map((block) => ({
            ...block,
            condition: patchCondition(block.condition),
            fields: mapFieldConditions(block.fields, patchCondition),
            ...(block.readAloudMessages && {
                readAloudMessages: mapMessageConditions(block.readAloudMessages, patchCondition)
            })
        }))
    };
};
const mapMessageConditions = (messages, patch) => messages.map((message) => {
    if (!message.condition)
        return message;
    const condition = FC.normalize(patch(message.condition));
    if (condition)
        return { ...message, condition };
    const { condition: _dropped, ...always } = message;
    return always;
});
export const stripUnresolvableConditions = (builder) => {
    const fieldIds = new Set();
    const collectIds = (fields) => {
        for (const f of fields) {
            if (f.id)
                fieldIds.add(f.id);
            if (f.properties)
                collectIds(f.properties);
            if (f.items?.properties)
                collectIds(f.items.properties);
        }
    };
    for (const block of builder.blocks ?? [])
        collectIds(block.fields);
    const isResolvable = (c) => (c.fieldId ? fieldIds.has(c.fieldId) : true);
    const strip = (c) => {
        if (!c)
            return c;
        const normalized = FC.normalize(c);
        if (!normalized)
            return undefined;
        return FC.filterLeaves(normalized, isResolvable);
    };
    return {
        ...builder,
        blocks: (builder.blocks ?? []).map((block) => ({
            ...block,
            condition: strip(block.condition),
            fields: mapFieldConditions(block.fields, strip),
            ...(block.readAloudMessages && {
                readAloudMessages: mapMessageConditions(block.readAloudMessages, strip)
            })
        }))
    };
};
export const hydrateConditionFieldNames = (builder, idToName) => mapBuilderConditions(builder, (c) => {
    let patched = c;
    if (c.fieldId) {
        const name = idToName.get(c.fieldId);
        if (name && name !== c.fieldName)
            patched = { ...patched, fieldName: name };
    }
    if ('expectedValueFieldId' in patched && patched.expectedValueFieldId) {
        const rhsName = idToName.get(patched.expectedValueFieldId);
        if (rhsName && rhsName !== String(patched.expectedValue)) {
            patched = { ...patched, expectedValue: rhsName };
        }
    }
    return patched;
});
const ensureFieldId = (field) => {
    const withId = field.id ? field : { ...field, id: crypto.randomUUID() };
    if (withId.properties)
        return { ...withId, properties: withId.properties.map(ensureFieldId) };
    if (withId.items?.properties)
        return { ...withId, items: { ...withId.items, properties: withId.items.properties.map(ensureFieldId) } };
    return withId;
};
export const prepareBlocks = (blocks) => {
    const withIds = blocks.map((block) => ({ ...block, fields: block.fields.map(ensureFieldId) }));
    const nameToId = new Map();
    for (const block of withIds)
        for (const field of block.fields)
            if (field.id && field.name)
                nameToId.set(field.name, field.id);
    return (mapBuilderConditions({ blocks: withIds }, (c) => {
        const patched = c.fieldId ? c : { ...c, fieldId: nameToId.get(c.fieldName) };
        if ('valueSource' in patched && patched.valueSource === 'field' && !patched.expectedValueFieldId) {
            const rhsId = nameToId.get(String(patched.expectedValue));
            if (rhsId)
                return { ...patched, expectedValueFieldId: rhsId };
        }
        return patched;
    }).blocks ?? withIds);
};
const resolveFieldValue = (condition, intakeData, blocks) => {
    if (condition.fieldId && blocks) {
        for (const b of blocks) {
            const path = findFieldPath(b.fields, condition.fieldId);
            if (path) {
                const blockData = intakeData[b.title];
                return blockData ? get(blockData, path) : undefined;
            }
        }
    }
    if (condition.fieldName) {
        for (const blockData of Object.values(intakeData)) {
            const v = get(blockData, condition.fieldName);
            if (v !== undefined)
                return v;
        }
    }
    return undefined;
};
// Reads its fields in any block, the way a block condition does
export const isFormConditionMet = (condition, intakeData, blocks) => {
    const normalized = FC.normalize(condition);
    if (!normalized)
        return true;
    const allFields = blocks?.flatMap((b) => b.fields);
    const resolveField = (name) => {
        for (const blockData of Object.values(intakeData)) {
            const v = get(blockData, name);
            if (v != null)
                return String(v);
        }
        return undefined;
    };
    const findFieldById = (id) => {
        if (!allFields)
            return undefined;
        const path = findFieldPath(allFields, id);
        if (!path)
            return undefined;
        for (const blockData of Object.values(intakeData)) {
            const v = get(blockData, path);
            if (v != null)
                return String(v);
        }
        return undefined;
    };
    const helpers = { resolveField, findFieldById };
    return FC.evaluate(normalized, (leaf) => evaluateCondition(leaf, resolveFieldValue(leaf, intakeData, blocks), helpers));
};
export const isConditionMet = (block, intakeData, blocks) => isFormConditionMet(block.condition, intakeData, blocks);
export const getOfferedOptions = (field, intakeData, blocks) => (field.enum ?? []).filter((option) => isFormConditionMet(field.enumWhen?.[option], intakeData, blocks));
// Whether its answer is needed when it's shown; its own condition decides whether it's shown
export const isFieldRequired = (field, intakeData, blocks) => !!field.required || (!!field.requiredWhen && isFormConditionMet(field.requiredWhen, intakeData, blocks));
const formatFieldPath = (pathParts) => {
    return pathParts.map((p) => (typeof p === 'number' ? `item ${p + 1}` : String(p))).join('.');
};
const formatZodReason = (error) => {
    if (error.code === 'invalid_value') {
        const enumError = error;
        if (enumError.values) {
            return `must be one of: ${enumError.values.join(', ')}`;
        }
    }
    if (error.code === 'invalid_type') {
        const typeError = error;
        return `expected ${typeError.expected}, got ${typeError.received}`;
    }
    return error.message;
};
const RANGE_MESSAGE_PREFIXES = ['Must be ', 'Date must be ', 'Time must be '];
const enhanceZodError = (error, blockTitle) => {
    const fieldPath = formatFieldPath(error.path);
    if (error.code === 'invalid_type') {
        const invalidTypeError = error;
        if (invalidTypeError.received === 'undefined' || invalidTypeError.received === undefined) {
            return { blockTitle, fieldPath, errorType: 'missing_field' };
        }
    }
    if (error.code === 'custom' && RANGE_MESSAGE_PREFIXES.some((p) => error.message.startsWith(p))) {
        return { blockTitle, fieldPath, errorType: 'out_of_range', reason: error.message };
    }
    if (error.code === 'too_small' || error.code === 'too_big') {
        return { blockTitle, fieldPath, errorType: 'out_of_range', reason: error.message };
    }
    return {
        blockTitle,
        fieldPath,
        errorType: 'invalid_field',
        reason: formatZodReason(error)
    };
};
const validateNestedField = (field, blockId, blockTitle, pathPrefix) => {
    const issues = [];
    const children = field.type === 'object'
        ? (field.properties ?? [])
        : field.type === 'list' && field.items?.type === 'object'
            ? (field.items.properties ?? [])
            : [];
    if (children.length === 0)
        return issues;
    const names = new Set();
    for (const child of children) {
        const displayPath = `${pathPrefix}.${child.name || '(unnamed)'}`;
        if (!child.name || child.name.trim() === '') {
            issues.push({ blockId, blockTitle, fieldName: displayPath, reason: 'Sub-field name is empty' });
            continue;
        }
        if (names.has(child.name)) {
            issues.push({
                blockId,
                blockTitle,
                fieldName: displayPath,
                reason: `Duplicate sub-field name in ${pathPrefix}`
            });
        }
        names.add(child.name);
        if (child.type === 'options' && child.enum) {
            const seen = new Set();
            for (const val of child.enum) {
                if (seen.has(val)) {
                    issues.push({
                        blockId,
                        blockTitle,
                        fieldName: displayPath,
                        reason: `Duplicate option value "${val}"`
                    });
                }
                seen.add(val);
            }
            if (child.enum.length === 0) {
                issues.push({
                    blockId,
                    blockTitle,
                    fieldName: displayPath,
                    reason: 'Options sub-field has no values'
                });
            }
        }
        // An object's part has no scope to evaluate a condition in; a list item's question reads its own item's answers.
        if (child.condition && field.type === 'object') {
            issues.push({
                blockId,
                blockTitle,
                fieldName: displayPath,
                reason: 'Conditions are not supported on nested fields'
            });
        }
        if (child.requiredWhen || child.enumWhen) {
            issues.push({
                blockId,
                blockTitle,
                fieldName: displayPath,
                reason: 'Option and required rules are not supported on nested fields'
            });
        }
        issues.push(...validateNestedField(child, blockId, blockTitle, displayPath));
    }
    return issues;
};
export const getBlockTitleIssue = (title) => {
    if (BLOCK_TITLE_INVALID_CHAR_RE.test(title)) {
        return 'Block name can only contain letters, numbers, spaces, and hyphens';
    }
    if (title.includes('  '))
        return "Block name can't have two spaces in a row";
    if (title.replace(/\s+/g, '_').length > MAX_BLOCK_TITLE_LENGTH) {
        return `Block name must be ${MAX_BLOCK_TITLE_LENGTH} characters or fewer`;
    }
};
const isListOfOptions = (field) => field.type === 'list' && field.items?.type === 'options';
// A list of options takes has any of / has none of, with values among its options; a one-answer check ("is", "is one
// of") would read the whole list as one answer. Undefined when the condition is neither.
const listOfOptionsIssues = (cond, gate, gateName) => {
    if (isPickCondition(cond)) {
        if (!isListOfOptions(gate))
            return [
                `"${OPERATION_LABEL[cond.operation]}" only applies to a list of options; "${gateName}" is ${gate.type}`
            ];
        if (cond.expectedValues.length === 0)
            return [`"${OPERATION_LABEL[cond.operation]}" requires at least one value`];
        const invalid = cond.expectedValues.filter((v) => !(gate.items?.enum ?? []).includes(v));
        return invalid.length > 0
            ? [`${invalid.map((v) => `"${v}"`).join(', ')} not in ${gateName}'s options`]
            : [];
    }
    if (isListOfOptions(gate) && !isPresenceOp(cond.operation))
        return [
            `"${OPERATION_LABEL[cond.operation]}" reads one answer; "${gateName}" is a list of options: use has any of or has none of`
        ];
    return undefined;
};
// What a condition part asks of the field it reads: an operator for that field's type, values among its options
const checkGateValue = (cond, gate, gateName, push) => {
    const listIssues = listOfOptionsIssues(cond, gate, gateName);
    if (listIssues) {
        listIssues.forEach(push);
    }
    else if (isPresenceOp(cond.operation)) {
        // answered/not_answered — valid for any field type
    }
    else if (isTextMatchCondition(cond)) {
        if (gate.type !== 'text')
            push(`"${OPERATION_LABEL[cond.operation]}" only applies to text fields; "${gateName}" is ${gate.type}`);
    }
    else if (isListCondition(cond)) {
        if (gate.type !== 'options' && gate.type !== 'text') {
            push(`"${cond.operation}" requires an Options or text field; "${gateName}" is ${gate.type}`);
        }
        else if (!Array.isArray(cond.expectedValues) || cond.expectedValues.length === 0) {
            push(`"${cond.operation}" requires at least one value`);
        }
        else if (gate.type === 'text') {
            // text fields have no enum — skip value validation
        }
        else {
            const validValues = gate.enum ?? [];
            const invalid = cond.expectedValues.filter((v) => typeof v !== 'string' || !validValues.includes(v));
            if (invalid.length > 0) {
                push(`Value${invalid.length === 1 ? '' : 's'} ${invalid.map((v) => `"${v}"`).join(', ')} not in ${gate.name}'s options`);
            }
        }
    }
    else if (isScalarCondition(cond) && !isCompareEquals(cond)) {
        if (gate.type === 'boolean') {
            if (typeof cond.expectedValue !== 'boolean') {
                push(`Gate value must be Yes or No`);
            }
        }
        else if (gate.type === 'options') {
            const validValues = gate.enum ?? [];
            const v = cond.expectedValue;
            if (typeof v !== 'string' || !validValues.includes(v)) {
                push(`Gate value "${String(v)}" is not one of ${gate.name}'s options`);
            }
        }
        else if (gate.type !== 'text') {
            push(`Gate field "${gateName}" is not Yes/No, Options, or text`);
        }
    }
};
export const validateFormBuilderConfig = (builder) => {
    const issues = [];
    if (!builder.blocks)
        return issues;
    const allFieldsSoFar = [];
    // A rule may read a question asked later: it reads as unanswered until then
    const everyField = builder.blocks.flatMap((b) => b.fields);
    for (const block of builder.blocks) {
        const titleIssue = getBlockTitleIssue(block.title);
        if (titleIssue) {
            issues.push({ blockId: block.id, blockTitle: block.title, reason: titleIssue });
        }
        const names = new Set();
        for (const field of block.fields) {
            if (!field.name || field.name.trim() === '') {
                issues.push({
                    blockId: block.id,
                    blockTitle: block.title,
                    fieldName: field.name || '(unnamed)',
                    reason: 'Field name is empty'
                });
                continue;
            }
            if (names.has(field.name)) {
                issues.push({
                    blockId: block.id,
                    blockTitle: block.title,
                    fieldName: field.name,
                    reason: `Duplicate field name in block`
                });
            }
            names.add(field.name);
            if (field.type === 'options' && field.enum) {
                const seen = new Set();
                for (const val of field.enum) {
                    if (seen.has(val)) {
                        issues.push({
                            blockId: block.id,
                            blockTitle: block.title,
                            fieldName: field.name,
                            reason: `Duplicate option value "${val}"`
                        });
                    }
                    seen.add(val);
                }
                if (field.enum.length === 0) {
                    issues.push({
                        blockId: block.id,
                        blockTitle: block.title,
                        fieldName: field.name,
                        reason: 'Options field has no values'
                    });
                }
            }
            if (field.condition) {
                const push = (reason) => issues.push({ blockId: block.id, blockTitle: block.title, fieldName: field.name, reason });
                const leafConditions = conditionLeafClauses(field.condition);
                for (const cond of leafConditions) {
                    const isSelf = conditionTargetsField(cond, field);
                    const gate = isSelf
                        ? undefined
                        : (findFieldDeep(cond, block.fields) ?? findFieldDeep(cond, allFieldsSoFar));
                    const gateName = gate?.name ?? cond.fieldName ?? cond.fieldId ?? 'unknown';
                    if (isSelf) {
                        push('Field cannot gate on itself');
                    }
                    else if (!gate) {
                        push(`Gates on missing field "${gateName}"`);
                    }
                    else {
                        checkGateValue(cond, gate, gateName, push);
                    }
                }
            }
            const pushRule = (reason) => issues.push({ blockId: block.id, blockTitle: block.title, fieldName: field.name, reason });
            for (const option of Object.keys(field.enumWhen ?? {})) {
                if (!field.enum?.includes(option))
                    pushRule(`Rule for "${option}", which is not one of its options`);
            }
            // A rule's parts are checked like a condition's; one reading its own field is no deadlock, so that stays allowed
            const rules = [field.requiredWhen, ...Object.values(field.enumWhen ?? {})].filter((rule) => !!rule);
            for (const leaf of rules.flatMap(conditionLeafClauses)) {
                const gate = findFieldDeep(leaf, everyField);
                if (!gate)
                    pushRule(`Rule reads missing field "${leaf.fieldName ?? leaf.fieldId ?? 'unknown'}"`);
                else
                    checkGateValue(leaf, gate, gate.name, (reason) => pushRule(`Rule: ${reason}`));
            }
            if (field.name && (field.type === 'object' || field.type === 'list')) {
                issues.push(...validateNestedField(field, block.id, block.title, field.name));
            }
        }
        allFieldsSoFar.push(...block.fields);
    }
    // Block conditions reference fields in PRIOR blocks; without these checks a typo in
    // fieldName silently behaves as "condition never met".
    const validateBlockCondition = (cond, earlierFields, push, label = 'Block condition', missingWhere = 'no earlier block defines that field') => {
        const gate = findFieldDeep(cond, earlierFields);
        const gateName = gate?.name ?? cond.fieldName ?? cond.fieldId ?? 'unknown';
        if (!gate) {
            push(`${label} references "${gateName}" but ${missingWhere}`);
            return;
        }
        const listIssues = listOfOptionsIssues(cond, gate, gateName);
        if (listIssues) {
            listIssues.forEach((reason) => push(`Block condition ${reason}`));
        }
        else if (isPresenceOp(cond.operation)) {
            // answered/not_answered — valid for any field type
        }
        else if (isTextMatchCondition(cond)) {
            if (gate.type !== 'text')
                push(`${label} "${OPERATION_LABEL[cond.operation]}" only applies to text fields; "${gateName}" is ${gate.type}`);
        }
        else if (isListCondition(cond)) {
            if (!Array.isArray(cond.expectedValues) || cond.expectedValues.length === 0) {
                push(`${label} "${cond.operation}" requires at least one value`);
            }
            else if (gate.type === 'options') {
                const validValues = gate.enum ?? [];
                const invalid = cond.expectedValues.filter((v) => typeof v !== 'string' || !validValues.includes(v));
                if (invalid.length > 0) {
                    push(`${label} value${invalid.length === 1 ? '' : 's'} ${invalid.map((v) => `"${v}"`).join(', ')} not in ${gateName}'s options`);
                }
            }
        }
        else if (isScalarCondition(cond) &&
            gate.type === 'boolean' &&
            typeof cond.expectedValue !== 'boolean') {
            push(`${label} gate "${gateName}" is Yes/No but expectedValue is not a boolean`);
        }
        else if (isScalarCondition(cond) && gate.type === 'options') {
            const validValues = gate.enum ?? [];
            const v = cond.expectedValue;
            if (typeof v !== 'string' || !validValues.includes(v)) {
                push(`${label} value "${String(v)}" is not one of ${gateName}'s options`);
            }
        }
    };
    for (let i = 0; i < builder.blocks.length; i++) {
        const block = builder.blocks[i];
        if (!block.condition)
            continue;
        const earlierFields = builder.blocks.slice(0, i).flatMap((b) => b.fields ?? []);
        const push = (reason) => issues.push({ blockId: block.id, blockTitle: block.title, reason });
        for (const cond of conditionLeafClauses(block.condition)) {
            validateBlockCondition(cond, earlierFields, push);
        }
    }
    // A message is said once its block saves, so its rule can read that block and earlier ones, never a later one
    for (let i = 0; i < builder.blocks.length; i++) {
        const block = builder.blocks[i];
        const savedFields = builder.blocks.slice(0, i + 1).flatMap((b) => b.fields ?? []);
        for (const message of block.readAloudMessages ?? []) {
            const push = (reason) => issues.push({ blockId: block.id, blockTitle: block.title, reason });
            const text = message.text.trim();
            if (!text)
                push('A say after save message has no text');
            // The agent would read it from the instructions too, so the caller would hear it twice
            else if (block.instructions?.includes(text))
                push('A say after save message is also in the instructions');
            if (!message.condition)
                continue;
            for (const cond of conditionLeafClauses(message.condition)) {
                validateBlockCondition(cond, savedFields, push, 'Say after save rule', 'neither this block nor an earlier one defines that field');
            }
        }
    }
    return issues;
};
// `ruleBlocks`: where option and required rules find their fields when only some blocks are checked
export const validateIntakeData = (builder, intakeData, ruleBlocks = builder.blocks) => {
    const errors = [];
    if (!builder.blocks || builder.blocks.length === 0) {
        return errors;
    }
    for (const block of builder.blocks) {
        const blockData = intakeData[block.title];
        const conditionMet = isConditionMet(block, intakeData, builder.blocks);
        const blockDataAsRecord = blockData ?? {};
        const blockContext = {
            blockData: blockDataAsRecord,
            fields: block.fields,
            intakeData,
            blocks: ruleBlocks
        };
        const blockValidator = createBlockValidator(block, blockContext);
        // If condition is met and block is missing but has required fields → missing block error
        if (conditionMet && !blockData) {
            const hasUnconditionalRequired = block.fields.some((field) => isFieldRequired(field, intakeData, ruleBlocks) && isFieldConditionMet(field, blockContext));
            if (hasUnconditionalRequired) {
                errors.push({ blockTitle: block.title, errorType: 'missing_block' });
                continue;
            }
        }
        // If data is provided, validate fields (regardless of condition)
        if (blockData) {
            const blockValidationResult = blockValidator.safeParse(blockData);
            if (!blockValidationResult.success) {
                const blockErrors = blockValidationResult.error.issues.map((error) => enhanceZodError(error, block.title));
                errors.push(...blockErrors);
            }
        }
    }
    return errors;
};
export const transformIntakeFormBuilderToSchema = (builder) => {
    if (!builder.blocks || builder.blocks.length === 0) {
        return {};
    }
    const result = {};
    for (const block of builder.blocks) {
        result[block.title] = {
            type: 'object',
            properties: block.fields.reduce((acc, field) => {
                acc[field.name] = transformIntakeFormFieldToSchema(field);
                return acc;
            }, {})
        };
    }
    return result;
};
//# sourceMappingURL=transform-schema.js.map